import { useMemo, useRef } from 'react';
import { SalesforceProduct, SalesforceProductVariant } from '~/lib/salesforce/types';
import { VariantGroup, VariantMatchQuality, VariantOption } from '~/features/product-details/types';
import { getVariantFamilyKey } from '~/features/product-details/utilities';

interface VariantMatch {
    variant?: SalesforceProductVariant;
    quality: VariantMatchQuality;
}

const matchesEvery = (
    variationValues: Record<string, string> | undefined,
    criteria: Record<string, string>,
) => {
    if (!variationValues) return false;

    return Object.entries(criteria).every(([key, value]) => variationValues[key] === value);
};

// Match variants by attributes while gauging proximity of match for UX.
const resolveVariant = (
    variants: SalesforceProductVariant[],
    currentValues: Record<string, string> | undefined,
    attributeId: string,
    value: string,
): VariantMatch => {
    const exact = variants.find((variant) =>
        matchesEvery(variant.variationValues, { ...currentValues, [attributeId]: value }),
    );

    if (exact) return { variant: exact, quality: 'exact' };

    const candidates = variants.filter((variant) =>
        matchesEvery(variant.variationValues, { [attributeId]: value }),
    );

    if (!candidates.length) return { quality: 'none' };

    const cheapest = candidates.reduce((lowest, candidate) =>
        candidate.price < lowest.price ? candidate : lowest,
    );

    return { variant: cheapest, quality: 'closest' };
};

// Find closest matching swatch image by product attributes.
const resolveSwatchUrl = (
    product: SalesforceProduct,
    attributeId: string,
    value: string,
): string | undefined => {
    const swatches = product.c_swatchImages;

    if (!Array.isArray(swatches)) return undefined;

    const match = swatches.find((swatch) => {
        const candidate =
            (swatch as Record<string, unknown>)[attributeId] ??
            swatch.supplierColour ??
            swatch.color;

        return typeof candidate === 'string' && candidate.toLowerCase() === value.toLowerCase();
    });

    return match?.url;
};

const buildImageUrlIndex = (groups: VariantGroup[] | undefined) => {
    const index = new Map<string, string>();

    groups?.forEach((group) =>
        group.options.forEach((option) => {
            if (option.imageUrl) index.set(`${group.id}:${option.value}`, option.imageUrl);
        }),
    );

    return index;
};

const getVariantGroups = (
    product: SalesforceProduct,
    previous?: VariantGroup[],
): VariantGroup[] => {
    const { variationAttributes, variationValues } = product;

    if (!variationAttributes?.length) return [];

    const variants = product.c_variants ?? [];

    /**
     * We will utilize the images from memory, if available, to avoid variant thumbnails from switching
     * when switching to other variants.
     */
    const knownImageUrls = buildImageUrlIndex(previous);

    return variationAttributes.reduce<VariantGroup[]>((groups, attribute) => {
        const values = attribute.values ?? [];

        if (values.length < 2) return groups;

        const selectedValue = variationValues?.[attribute.id];

        const options = values.map<VariantOption>((entry) => {
            const { variant, quality } = resolveVariant(
                variants,
                variationValues,
                attribute.id,
                entry.value,
            );

            return {
                value: entry.value,
                label: entry.name ?? entry.value,
                productId: variant?.productId,
                href: variant?.c_pageURL,
                imageUrl:
                    knownImageUrls.get(`${attribute.id}:${entry.value}`) ??
                    resolveSwatchUrl(product, attribute.id, entry.value),
                price: variant?.price,
                isSelected: entry.value === selectedValue,
                isOrderable: !!entry.orderable,
                matchQuality: quality,
            };
        });

        groups.push({
            id: attribute.id,
            name: attribute.name,
            selectedValue,
            options,
        });

        return groups;
    }, []);
};

export const useVariantGroups = (product: SalesforceProduct | undefined): VariantGroup[] => {
    const previousRef = useRef<{ familyKey: string; groups: VariantGroup[] }>();

    return useMemo(() => {
        if (!product) return [];

        const familyKey = getVariantFamilyKey(product);

        /**
         * This will primarily be utilized to prevent visual shifts in our product variant selector
         * when switching variants by saving the previous instances.
         */
        const previous =
            previousRef.current?.familyKey === familyKey ? previousRef.current.groups : undefined;

        const groups = getVariantGroups(product, previous);

        previousRef.current = { familyKey, groups };

        return groups;
    }, [product]);
};
